import type { User } from '@iheartjane/data-access';
import { EventNames } from '@iheartjane/tracking';
import type { Nullish } from '@iheartjane/types';

import type { PaymentOptions } from './hooks/usePaymentOptions';
import { PaymentMethods } from './hooks/usePaymentOptions';

/**
 * The decisions the checkout submit chain makes, as pure functions. Submitting
 * runs three stages — recaptcha, then MFA, then the checkout request — and each
 * gate below answers whether its stage can be skipped.
 */

// A switch rather than a lookup map: it keeps the return type narrowed to the
// two events `track` accepts, where a Record widens it to all of EventNames.
export const trackingEventForPayment = (selected: Nullish<PaymentOptions>) => {
  switch (selected) {
    case PaymentMethods.janePay:
      return EventNames.ClickedPayWithJanePay;
    case PaymentMethods.stronghold:
      return EventNames.ClickedPayWithStronghold;
    default:
      return null;
  }
};

/**
 * Providers that run their own fraud checks, so a second recaptcha adds friction
 * without adding protection — and, conversely, the providers we re-verify the
 * shopper for when their last MFA attempt has gone stale.
 */
const SELF_GUARDED_PAYMENTS: PaymentOptions[] = [
  PaymentMethods.janePay,
  PaymentMethods.stronghold,
];

export const shouldSkipRecaptcha = ({
  canpayV2RemotepayEnabled,
  isHeadlessKioskMode,
  selectedPaymentOption,
}: {
  canpayV2RemotepayEnabled: Nullish<boolean>;
  isHeadlessKioskMode: boolean;
  selectedPaymentOption: Nullish<PaymentOptions>;
}) => {
  // Kiosks are physically supervised, and the backend skips verification too.
  if (isHeadlessKioskMode) return true;
  if (!selectedPaymentOption) return false;
  if (SELF_GUARDED_PAYMENTS.includes(selectedPaymentOption)) return true;

  return (
    selectedPaymentOption === PaymentMethods.canpay &&
    Boolean(canpayV2RemotepayEnabled)
  );
};

/**
 * External users verify their phone with their own identity provider, so Jane
 * never asks them for a code.
 * @todo An absent `janeUser` reads as unverified, which means an authenticated
 * shopper sees MFA when /users/me has not resolved yet. Preserved as-is here;
 * fixing it is a behavior change to the live checkout flow.
 */
export const phoneNeedsVerification = (user: Nullish<User>) =>
  !user?.phone_verified_at && !user?.external_user;

/**
 * Three independent reasons to launch MFA before submitting: the shopper typed a
 * new number, an authenticated shopper has never verified the one on file, or an
 * authenticated shopper is prepaying and their last verification has gone stale.
 * Only the last is "extraneous" enough to be flag-disabled.
 *
 * Even then it is only extraneous at a store the shopper has paid at before:
 * Aeropay itself re-verifies on first use at a new merchant, so skipping the
 * code there would only push the challenge further down the flow.
 */
export const shouldLaunchMfa = ({
  authenticated,
  hasNewPhone,
  hasUsedJanePayAtStore,
  needsPhoneVerification,
  requiresMfa,
  selectedPaymentOption,
  skipExtraneousMfa,
}: {
  authenticated: Nullish<boolean>;
  hasNewPhone: Nullish<boolean>;
  /**
   * Whether this shopper has already paid with Jane Pay at this store. An
   * unresolved or failed lookup must arrive here as `false`, so an unknown
   * shopper is re-verified rather than waved through.
   */
  hasUsedJanePayAtStore: boolean;
  needsPhoneVerification: boolean;
  requiresMfa: boolean;
  selectedPaymentOption: Nullish<PaymentOptions>;
  /** Maps to the feature flag that drops MFA a returning Jane Pay shopper does not need. */
  skipExtraneousMfa: boolean;
}) => {
  if (hasNewPhone) return true;
  if (!authenticated) return false;
  if (needsPhoneVerification) return true;
  if (selectedPaymentOption === PaymentMethods.stronghold && requiresMfa)
    return true;

  return (
    selectedPaymentOption === PaymentMethods.janePay &&
    requiresMfa &&
    !(skipExtraneousMfa && hasUsedJanePayAtStore)
  );
};
